import { createAxios } from '../lib/axiosFactory';
import { dataCollectionParser, dataParser } from '../lib/response/dataParser';
import { errorParser } from '../lib/response/errorParser';
import { ActionResponse } from '../lib/interfaces/actionResponse';
import _ from 'lodash';
import { MessageDirection } from '../lib/enums/message';

export const state = {
  threads: [],
  messages: [],
  currentThreadContactId: undefined,
  unreadThreadsCount: 0
};

export const getters = {
  latestIncomingMessage(state) {
    return state.messages
      .filter(x => x.direction != MessageDirection.Outgoing)
      .slice(-1)[0];
  }
};

export const mutations = {
  setThreads(state: any, threads: any[]): void {
    let threadsWithContacts = threads;

    // do not show threads without an associated contact
    threadsWithContacts = threads?.filter(x => x.contact);
    state.threads = threadsWithContacts;
  },

  setMessages(state: any, { messages, contactId }): void {
    state.messages = messages;
    state.currentThreadContactId = contactId;
  },

  setUnreadThreadsCount(state: any, { unreadThreadsCount }): void {
    state.unreadThreadsCount = unreadThreadsCount;
  },

  addMessage(state: any, message): void {
    state.messages.push(message);
  },

  setThreadAsRead(state: any, { contactId }): void {
    state.threads.find(x => x.contactId === contactId).isRead = true;
  }
};

export const actions = {
  async setUnreadThreadsCount({ commit }): Promise<void> {
    commit('setUnreadThreadsCount', 3);
  },

  async loadThreads({ commit }): Promise<ActionResponse> {
    try {
      const response: any = await createAxios(process.env.DataApiBaseUrl).post(
        '/data-api/graphql',
        {
          query: `{ 
            threads
            { 
                id, 
                clientId, 
                contactId, 
                lastIncomingMessageId,
                lastIncomingMessageUpdatedAt,
                lastOutgoingMessageUpdatedAt,
                lastMessageUpdatedAt
                isRead,
                contact {
                    id,
                    firstName,
                    lastName,
                    phone
                }
            } 
          }`
        },
        {
          params: {
            mock: false
          }
        }
      );

      commit('setThreads', response.data.data.threads);

      return { success: true, errors: {}, body: response.data.data };
    } catch (error) {
      return {
        success: false,
        errors: errorParser(error),
        body: error.response.data
      };
    }
  },

  async loadMessages({ commit }, contactId): Promise<ActionResponse> {
    try {
      const response: any = await createAxios(process.env.DataApiBaseUrl).post(
        '/data-api/graphql',
        {
          query: `{
            thread(contactId:"${contactId}") 
            { 
              messages 
              {
                    id, 
                    contactId, 
                    campaignId, 
                    campaignName,
                    provider, 
                    status, 
                    body, 
                    direction, 
                    type,
                    mmsTemplateId,
                    templateId,
                    updatedAt,
                    sentByUserId,
                    sentByUserName
              } 
            }
          }`
        },
        {
          params: {
            mock: false
          }
        }
      );

      commit('setMessages', {
        messages: response.data.data.thread.messages,
        contactId
      });

      return { success: true, errors: {}, body: response.data.data };
    } catch (error) {
      return {
        success: false,
        errors: errorParser(error),
        body: error.response.data
      };
    }
  },

  async loadUnreadThreadsCount({ commit }): Promise<ActionResponse> {
    try {
      const response: any = await createAxios(process.env.DataApiBaseUrl).post(
        '/data-api/graphql',
        {
          query: `{ unreadThreadsCount }`
        },
        {
          params: {
            mock: false
          }
        }
      );

      commit('setUnreadThreadsCount', response.data.data);

      return { success: true, errors: {}, body: response.data.data };
    } catch (error) {
      return {
        success: false,
        errors: errorParser(error),
        body: error.response.data
      };
    }
  },

  async sendSms(
    { commit },
    { contactId, mmsTemplateId, body }
  ): Promise<ActionResponse> {
    try {
      const response: any = await createAxios().post(
        '/backend/contact-sms-sends-create',
        {
          data: {
            type: 'sms',
            attributes: {
              body,
              mmsTemplateId
            }
          }
        },
        {
          params: {
            id: contactId
          }
        }
      );
      return { success: true, errors: {}, body: response.data.data };
    } catch (error) {
      return {
        success: false,
        errors: errorParser(error),
        body: error.response.data
      };
    }
  }
};
